import React, { useState } from 'react'
import { normalizeTagInput, tagAlreadyExists } from './tagUtils'
import styles from './TagInput.module.css'

export type TagTone = 'default' | 'positive' | 'negative'

const toneChip: Record<TagTone, string> = {
  default: styles.chipDefault,
  positive: styles.chipPositive,
  negative: styles.chipNegative,
}

export type TagInputProps = {
  id: string
  tags: string[]
  onChange: (tags: string[]) => void
  /** Visual tone — matches task tags (default) or mood positive/negative lists. */
  tone?: TagTone
  disabled?: boolean
  className?: string
  placeholderEmpty?: string
  placeholderMore?: string
  'aria-label'?: string
  'aria-labelledby'?: string
}

/**
 * Chip list + inline field — same interaction as task details tags (Enter commits one tag, # stripped).
 */
const TagInput = ({
  id,
  tags,
  onChange,
  tone = 'default',
  disabled = false,
  className,
  placeholderEmpty = 'Add tag',
  placeholderMore = '+ tag',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: TagInputProps) => {
  const [draft, setDraft] = useState('')

  const commitDraft = () => {
    if (disabled) return
    const normalized = normalizeTagInput(draft)
    setDraft('')
    if (!normalized) return
    if (tagAlreadyExists(tags, normalized)) return
    onChange([...tags, normalized])
  }

  const remove = (tag: string) => {
    if (disabled) return
    onChange(tags.filter((t) => t !== tag))
  }

  const chipTone = toneChip[tone]

  return (
    <div className={`${styles.chips} ${className ?? ''}`}>
      {tags.map((t, i) => (
        <span
          key={`${t}-${i}`}
          className={`${styles.chipBase} ${chipTone}`}
        >
          <span className={styles.chipText}>#{t}</span>
          <button
            type="button"
            className={styles.chipRemove}
            aria-label={`Remove tag ${t}`}
            title={`Remove ${t}`}
            disabled={disabled}
            onClick={() => remove(t)}
          >
            <svg
              className={styles.chipRemoveIcon}
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden
            >
              <path
                d="M2.5 2.5l7 7M9.5 2.5l-7 7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </span>
      ))}
      <input
        id={id}
        type="text"
        className={styles.textInput}
        disabled={disabled}
        placeholder={tags.length === 0 ? placeholderEmpty : placeholderMore}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commitDraft()
          } else if (e.key === 'Escape') {
            setDraft('')
          }
        }}
        onBlur={commitDraft}
      />
    </div>
  )
}

export default TagInput
